/**
 * Main-thread facade for opencv.worker.js.
 * React waits only for OPENCV_READY here — never touches window.cv or cv.* APIs.
 *
 * Call initOpenCVWorker() when the user starts Add Patient / patient screening.
 * OpenCV script caching is owned by the service worker (precache + /opencv/ CacheFirst).
 */

import { warmOpenCvScriptCache, formatOpenCvInitError } from '../utils/opencvOfflineCache';
import { applyUpdateIfWaiting } from '../serviceWorkerRegistration';

let _seq = 0;
const _nextId = () => ++_seq;

/**
 * The worker's own OpenCV init timer (opencv.worker.js RUNTIME_TIMEOUT_MS) lives INSIDE the
 * worker — useless if the browser kills/freezes that worker thread under memory pressure
 * (a known iOS/WebKit failure mode that fires no `error` event at all). These watchdogs run
 * on the main thread instead, which keeps ticking regardless of the worker's state.
 */
const INIT_WATCHDOG_MS = 25000;
const OP_WATCHDOG_MS = 30000;

/** @type {Worker|null} */
let _worker = null;
/** @type {Promise<void>|null} */
let _readyPromise = null;
/** @type {Map<number, { resolve: Function, reject: Function }>} */
const _pending = new Map();

/** @type {((value?: void) => void)|null} */
let _resolveReady = null;
/** @type {((reason?: unknown) => void)|null} */
let _rejectReady = null;

function _toInitError(err) {
    if (err instanceof Error && err.message.includes('Connect to the internet')) {
        return err;
    }
    return new Error(formatOpenCvInitError(err));
}

function _rejectAllPending(err) {
    for (const rec of _pending.values()) {
        rec.reject(err);
    }
    _pending.clear();
}

/**
 * Terminate worker and clear in-flight state.
 * Only init's final `.catch()` or explicit terminate should set clearReadyPromise.
 */
function _cleanupWorkerState({
    rejectReady = false,
    rejectPending = false,
    clearReadyPromise = false,
    err = null,
} = {}) {
    if (_worker) {
        try {
            _worker.terminate();
        } catch (_) { /* ignore */ }
    }
    _worker = null;

    if (rejectPending && err) {
        _rejectAllPending(err);
    }

    if (rejectReady && err && _rejectReady) {
        _rejectReady(err);
    }
    _resolveReady = null;
    _rejectReady = null;

    if (clearReadyPromise) {
        _readyPromise = null;
    }
}

/** Reject the current init attempt; does not clear _readyPromise (init retry owns that). */
function _abortWorkerAttempt(err) {
    const formatted = _toInitError(err);
    _rejectAllPending(formatted);
    if (_rejectReady) {
        _rejectReady(formatted);
    }
    _resolveReady = null;
    _rejectReady = null;
    if (_worker) {
        try {
            _worker.terminate();
        } catch (_) { /* ignore */ }
    }
    _worker = null;
}

function _attachWorkerHandlers(worker) {
    worker.addEventListener('message', (ev) => {
        const msg = ev.data || {};
        if (msg.type === 'OPENCV_READY') {
            if (_resolveReady) {
                _resolveReady();
                _resolveReady = null;
                _rejectReady = null;
            }
            return;
        }
        if (msg.type === 'OPENCV_ERROR') {
            console.error('[opencv-rpc] OPENCV_ERROR received', msg.error?.message);
            _abortWorkerAttempt(new Error(msg.error?.message || 'OpenCV failed to initialize'));
            return;
        }

        const rec = _pending.get(msg.id);
        if (!rec) return;
        _pending.delete(msg.id);
        if (msg.type === 'ERROR') {
            rec.reject(new Error(msg.message || 'OpenCV worker error'));
        } else {
            rec.resolve(msg);
        }
    });
    worker.addEventListener('error', (ev) => {
        console.error('[opencv-rpc] worker error event', ev.message);
        _abortWorkerAttempt(new Error(ev.message || 'OpenCV worker script error'));
    });
}

function _spawnWorker() {
    const worker = new Worker(
        new URL('../workers/opencv.worker.js', import.meta.url),
        { type: 'classic' }
    );
    _attachWorkerHandlers(worker);
    return worker;
}

function _waitForWorkerReady() {
    return new Promise((resolve, reject) => {
        let settled = false;
        const watchdogId = setTimeout(() => {
            if (settled) return;
            settled = true;
            console.error(`[opencv-rpc] main-thread watchdog: worker never sent OPENCV_READY within ${INIT_WATCHDOG_MS}ms — likely killed/frozen by the browser under memory pressure. Terminating.`);
            _abortWorkerAttempt(new Error('OpenCV worker did not respond — the browser may have closed it to free up memory. Please retry.'));
        }, INIT_WATCHDOG_MS);

        _resolveReady = () => {
            if (settled) return;
            settled = true;
            clearTimeout(watchdogId);
            resolve();
        };
        _rejectReady = (err) => {
            if (settled) return;
            settled = true;
            clearTimeout(watchdogId);
            reject(err);
        };
        _worker = _spawnWorker();
    });
}

function _send(type, payload = {}, transferables = []) {
    return new Promise((resolve, reject) => {
        if (!_worker) {
            reject(new Error('OpenCV worker is not initialized'));
            return;
        }

        const id = _nextId();

        const watchdogId = setTimeout(() => {
            console.error(`[opencv-rpc] main-thread watchdog: ${type} (id=${id}) got no reply within ${OP_WATCHDOG_MS}ms — worker likely killed/frozen by the browser. Terminating.`);
            _readyPromise = null; // force a fresh worker on the next call
            _abortWorkerAttempt(new Error(`OpenCV worker stopped responding during ${type} — the browser may have closed it to free up memory. Please retry.`));
        }, OP_WATCHDOG_MS);

        _pending.set(id, {
            resolve: (msg) => { clearTimeout(watchdogId); resolve(msg); },
            reject: (err) => { clearTimeout(watchdogId); reject(err); },
        });

        try {
            _worker.postMessage({ type, id, ...payload }, transferables);
        } catch (err) {
            clearTimeout(watchdogId);
            _pending.delete(id);
            console.error(`[opencv-rpc] postMessage threw for ${type} (id=${id})`, err);
            reject(err);
        }
    });
}

/**
 * Start the OpenCV worker and wait for OPENCV_READY.
 * Assigns _readyPromise immediately to avoid concurrent double-init races.
 */
export function initOpenCVWorker() {
    if (_readyPromise) {
        return _readyPromise;
    }

    _readyPromise = (async () => {
        try {
            await warmOpenCvScriptCache();
            await _waitForWorkerReady();
        } catch (firstErr) {
            _abortWorkerAttempt(firstErr);
            await warmOpenCvScriptCache({ force: true });
            await _waitForWorkerReady();
        }
    })();

    return _readyPromise.catch((err) => {
        _readyPromise = null;
        // A stale service worker still serving a previously built (larger-heap) opencv.js is a
        // known cause of init hangs on constrained phones — self-heal if a newer SW is waiting.
        applyUpdateIfWaiting();
        throw err instanceof Error ? err : _toInitError(err);
    });
}

/** @returns {Promise<void>} */
export function waitForOpenCvReady() {
    return initOpenCVWorker();
}

export function terminateOpenCVWorker() {
    const err = new Error('OpenCV worker terminated');
    _cleanupWorkerState({
        rejectReady: true,
        rejectPending: true,
        clearReadyPromise: true,
        err,
    });
}

function _sliceBuffer(view) {
    return view.buffer.slice(view.byteOffset, view.byteOffset + view.byteLength);
}

/**
 * @param {ImageData} imageData
 * @returns {Promise<Uint8ClampedArray>}
 */
export async function imageDataToRgb(imageData) {
    await waitForOpenCvReady();
    const { width, height } = imageData;
    const buffer = _sliceBuffer(imageData.data);
    const msg = await _send(
        'IMAGE_DATA_TO_RGB',
        { width, height, buffer },
        [buffer]
    );
    return new Uint8ClampedArray(msg.buffer);
}

/**
 * @param {Uint8ClampedArray|Uint8Array} rgb
 * @param {number} width
 * @param {number} height
 * @returns {Promise<Uint8ClampedArray>}
 */
export async function convertToGrayscaleFromRgb(rgb, width, height) {
    await waitForOpenCvReady();
    const buffer = _sliceBuffer(rgb);
    const msg = await _send(
        'CONVERT_TO_GRAYSCALE_FROM_RGB',
        { width, height, buffer },
        [buffer]
    );
    return new Uint8ClampedArray(msg.buffer);
}

/**
 * @param {Uint8ClampedArray|Uint8Array} rgb
 * @returns {Promise<Uint8ClampedArray>}
 */
export async function resizeRgbWithPad(rgb, width, height, targetSize, paddingColor = [0, 0, 0]) {
    await waitForOpenCvReady();
    const buffer = _sliceBuffer(rgb);
    const msg = await _send(
        'RESIZE_RGB_WITH_PAD',
        { width, height, targetSize, paddingColor, buffer },
        [buffer]
    );
    return new Uint8ClampedArray(msg.buffer);
}
